import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { fetchBlogTopics } from '../../services/blogApi';
import { useTheme } from '../../contexts/ThemeContext';

const MODE_AUTO = 'auto';
const MODE_PRESET = 'preset';
const MODE_CUSTOM = 'custom';

export function buildGeneratePayload({ mode, preset, customPrompt, extraDirection }) {
  if (mode === MODE_CUSTOM) {
    const text = (customPrompt || '').trim();
    return {
      topic: text || undefined,
      direction: text || undefined,
      valid: Boolean(text),
      error: text ? null : 'Enter a custom prompt',
    };
  }
  if (mode === MODE_PRESET) {
    const notes = (extraDirection || '').trim();
    return {
      topic: preset || undefined,
      direction: notes || undefined,
      valid: Boolean(preset),
      error: preset ? null : 'Pick a keyword',
    };
  }
  const notes = (extraDirection || '').trim();
  return {
    topic: undefined,
    direction: notes || undefined,
    valid: true,
    error: null,
  };
}

/**
 * Topic / direction controls for blog generation.
 * Call ref.current.getPayload() before enqueueing.
 */
const BlogGenerateControls = forwardRef(function BlogGenerateControls(
  { token, compact = false },
  ref
) {
  const { colors } = useTheme();
  const [topics, setTopics] = useState([]);
  const [mode, setMode] = useState(MODE_AUTO);
  const [preset, setPreset] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [extraDirection, setExtraDirection] = useState('');
  const [loadError, setLoadError] = useState(null);

  useImperativeHandle(
    ref,
    () => ({
      getPayload: () =>
        buildGeneratePayload({ mode, preset, customPrompt, extraDirection }),
    }),
    [mode, preset, customPrompt, extraDirection]
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBlogTopics(token);
        if (cancelled) return;
        setTopics(data.topics || []);
        setLoadError(null);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Failed to load topics');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const muted = colors.textMuted || colors.textLight || '#94a3b8';
  const border = '1px solid rgba(148,163,184,0.35)';
  const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.65rem',
    borderRadius: '6px',
    border,
    background: colors.bgSecondary || 'transparent',
    color: colors.text,
    fontSize: '0.9rem',
    fontFamily: 'inherit',
  };

  return (
    <div
      className="blog-generate-controls"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '0.65rem' : '0.85rem',
        marginTop: compact ? 0 : '1rem',
        padding: compact ? 0 : '1rem',
        borderRadius: compact ? 0 : '8px',
        border: compact ? 'none' : border,
        maxWidth: compact ? '100%' : '28rem',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.85rem' }}>
        <span style={{ color: muted }}>Starting direction</span>
        <select
          value={mode === MODE_PRESET ? `preset:${preset}` : mode}
          onChange={(e) => {
            const v = e.target.value;
            if (v === MODE_AUTO) {
              setMode(MODE_AUTO);
              setPreset('');
            } else if (v === MODE_CUSTOM) {
              setMode(MODE_CUSTOM);
            } else if (v.startsWith('preset:')) {
              setMode(MODE_PRESET);
              setPreset(v.slice('preset:'.length));
            }
          }}
          style={inputStyle}
        >
          <option value={MODE_AUTO}>Auto (outlook, last week, or week ahead)</option>
          <option value={MODE_CUSTOM}>Custom prompt…</option>
          {topics.length > 0 && (
            <optgroup label="Keyword map">
              {topics.map((t) => (
                <option key={t.keyword} value={`preset:${t.keyword}`}>
                  {t.keyword} ({t.funnel_stage})
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </label>

      {mode === MODE_CUSTOM && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.85rem' }}>
          <span style={{ color: muted }}>Custom prompt</span>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            rows={4}
            placeholder="e.g. Write about why daily loss limits fail when they’re only a journal reminder, not enforced at order time…"
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.45 }}
          />
        </label>
      )}

      {(mode === MODE_AUTO || mode === MODE_PRESET) && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.85rem' }}>
          <span style={{ color: muted }}>Optional notes (angle, competitor, audience)</span>
          <textarea
            value={extraDirection}
            onChange={(e) => setExtraDirection(e.target.value)}
            rows={compact ? 2 : 3}
            placeholder="Optional — steer the angle. Auto will not write a tool comparison."
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.45 }}
          />
        </label>
      )}

      {loadError && (
        <p style={{ margin: 0, color: colors.danger || '#f87171', fontSize: '0.8rem' }}>{loadError}</p>
      )}
    </div>
  );
});

export default BlogGenerateControls;
